import React, { useContext } from 'react';
import { LoadingOverlay, Skeleton } from '@mantine/core';
import type { FullContest } from '@betterpool/api-types/contests-service';
import useTranslation from 'next-translate/useTranslation';
import { useIsMutating } from '@tanstack/react-query';
import { useMediaQuery } from 'react-responsive';
import { useRouter } from 'next/router';

import { UserContext } from '~/components/providers/UserProvider';
import dt from '~/testing';
import { ButtonLink, LinkButton } from '~/domains/design-system/Button';
import { Routes } from '~/domains/common/constants/routes';

import useStyles from './EntryButton.styles';

type EditEntrantsButtonProps = {
  className?: string;
  contest: FullContest;
  isHidden?: boolean;
  isEditButtonHidden?: boolean;
};

function EditEntrantsButton({
  className,
  contest,
  isHidden,
  isEditButtonHidden = false,
}: EditEntrantsButtonProps) {
  const { classes, theme } = useStyles();
  const router = useRouter();
  const { t } = useTranslation('contest');
  const { user } = useContext(UserContext);
  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.sm });

  const isLoading = Boolean(useIsMutating(['createOrJoinEntry']));

  if (isHidden) return null;

  if (isTablet) {
    return (
      <Skeleton
        visible={user && isLoading}
        h={40}
        w={`${'max-content'}`}
        className={isEditButtonHidden ? classes.headerEdit : classes.headerEditEntrants}
      >
        <LoadingOverlay visible={user && isLoading} loaderProps={{ size: 'sm' }} />
        <LinkButton
          variant="text"
          size="medium"
          data-test-id={dt.contestDetail.page.header.actions.edit}
          onClick={() => router.push(Routes.entrantsManager(contest.id))}
        >
          {t('detail.header.actions.editEntrants')}
        </LinkButton>
      </Skeleton>
    );
  }

  return (
    <Skeleton visible={user && isLoading} h={40} className={className}>
      <LoadingOverlay visible={user && isLoading} loaderProps={{ size: 'sm' }} />
      <ButtonLink
        variant="secondary-stroke"
        size="medium"
        data-test-id={dt.contestDetail.page.header.actions.edit}
        href={Routes.entrantsManager(contest.id)}
      >
        {t('detail.header.actions.editEntrants')}
      </ButtonLink>
    </Skeleton>
  );
}

export default EditEntrantsButton;
